import { useState } from "react";
import { ArrowUpRight, Check, Info } from "lucide-react";
import { chapters } from "@/data/chapters";
import { Reveal } from "@/components/Reveal";
import { scrollToId } from "@/config";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

export default function BentoGrid() {
  const [active, setActive] = useState(null);

  return (
    <section id="conteudo" className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
      <div className="mb-14 grid gap-6 md:grid-cols-2 md:items-end">
        <Reveal>
          <p className="mb-4 text-xs font-extrabold uppercase tracking-[0.22em] text-flame">
            Dentro do e-book
          </p>
          <h2 className="font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl">
            15 capítulos para transformar informação em cuidado.
          </h2>
        </Reveal>
        <Reveal delay={0.12}>
          <p className="max-w-md text-base leading-relaxed text-ink md:ml-auto">
            Toque em um capítulo para ver um resumo do que você vai aprender — da
            comunicação à rotina, da escola aos direitos.
          </p>
        </Reveal>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-12">
        {chapters.map((c, i) => (
          <Reveal key={c.n} delay={(i % 3) * 0.08} className={`${c.span}`}>
            <button
              data-testid={`chapter-card-${c.n}`}
              onClick={() => setActive(c)}
              className="group flex h-full w-full flex-col rounded-3xl border border-[#E2E8F0] bg-white p-8 text-left shadow-[0_10px_36px_-18px_rgba(10,25,47,0.18)] transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_24px_48px_-18px_rgba(10,25,47,0.28)]"
            >
              <div className="mb-6 flex items-start justify-between">
                <span className={`grid h-12 w-12 place-items-center rounded-2xl ${c.tint}`}>
                  <c.icon className="h-6 w-6" strokeWidth={2.2} />
                </span>
                <span className="font-display text-sm font-bold text-navy/25">{c.n}</span>
              </div>
              <h3 className="mb-2 font-display text-2xl font-semibold leading-snug text-navy">
                {c.title}
              </h3>
              <p className="mb-6 text-sm leading-relaxed text-ink">{c.desc}</p>
              <span className="mt-auto inline-flex items-center gap-1 text-xs font-extrabold uppercase tracking-[0.16em] text-brand transition-colors duration-200 group-hover:text-flame">
                Ver resumo
                <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
            </button>
          </Reveal>
        ))}
      </div>

      <Dialog open={!!active} onOpenChange={() => setActive(null)}>
        <DialogContent data-testid="chapter-dialog" className="max-w-lg rounded-3xl bg-sand p-8">
          {active && (
            <>
              <DialogHeader>
                <span className={`mb-4 grid h-12 w-12 place-items-center rounded-2xl ${active.tint}`}>
                  <active.icon className="h-6 w-6" strokeWidth={2.2} />
                </span>
                <DialogDescription className="font-display text-sm font-bold text-navy/30">
                  Capítulo {active.n}
                </DialogDescription>
                <DialogTitle className="font-display text-3xl font-bold text-navy">
                  {active.title}
                </DialogTitle>
              </DialogHeader>
              <ul className="mt-4 space-y-3">
                {active.bullets.map((b) => (
                  <li key={b} className="flex gap-3 text-sm leading-relaxed text-ink">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-green-600" strokeWidth={3} />
                    {b}
                  </li>
                ))}
              </ul>
              {active.note && (
                <p className="mt-5 flex gap-2.5 rounded-2xl bg-brandlight p-4 text-sm font-semibold text-navy">
                  <Info className="h-4 w-4 shrink-0 text-brand" />
                  {active.note}
                </p>
              )}
              <button
                data-testid="chapter-dialog-cta"
                onClick={() => {
                  setActive(null);
                  scrollToId("comprar");
                }}
                className="mt-6 w-full rounded-full bg-flame px-6 py-3.5 text-sm font-extrabold uppercase tracking-wide text-white transition-all duration-200 hover:scale-[1.02] hover:bg-[#c2410c]"
              >
                Quero o e-book completo
              </button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
